import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FaEye, FaEyeSlash, FaChevronDown, FaChevronUp, FaArrowRight } from 'react-icons/fa';
import { API } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { updateUserPreferences } from '../utils/geolocation';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '../utils/errorMessage';

const LoginPage = ({ onLogin }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingGoogle, setProcessingGoogle] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // ── Guest-first vs. direkte-login (per user 08-Jul-2026) ──
  // Landing-hero'en er default-visningen på /login. Når en bruger klikker
  // "Log ind" i topmenuen fra inde i appen, navigeres til
  // `/login?mode=login` — så springer vi hero + primær CTA over og viser
  // login-formularen direkte-udfoldet. Ingen "baglæns"-følelse.
  //
  // Bemærk: vi kan IKKE stole på localStorage.slushfinder_session_id som
  // signal for "har været her før", fordi App.js kalder getSessionId() ved
  // hver mount og altid opretter en ID — også for førstegangsbesøgende.
  // Eneste pålidelige signal er det eksplicitte query-param fra topbaren.
  const wantsLoginMode = new URLSearchParams(location.search).get('mode') === 'login';
  const showLandingHero = !wantsLoginMode;
  const [loginExpanded, setLoginExpanded] = useState(wantsLoginMode);

  const handleExploreAsGuest = () => {
    // Prototype (per user 08-Jul-2026): gæsten sendes DIREKTE til
    // opskriftssiden i stedet for forsiden. `?welcome=1` fortæller
    // RecipesPage at der skal vises en lille velkomst-banner øverst.
    // Fjerner et unødvendigt hero-trin — brugeren har allerede sagt ja
    // til at udforske og skal derfor møde selve produktet med det samme.
    navigate('/recipes?welcome=1');
  };

  // Generate device fingerprint
  const getDeviceFingerprint = () => {
    const data = [
      navigator.userAgent,
      navigator.language,
      screen.width,
      screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset()
    ].join('|');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return 'device_' + Math.abs(hash).toString(36);
  };
  
  const getDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/i.test(ua)) return 'iPhone';
    if (/iPad/i.test(ua)) return 'iPad';
    if (/Android/i.test(ua)) return 'Android Device';
    if (/Mac/i.test(ua)) return 'Mac';
    if (/Windows/i.test(ua)) return 'Windows PC';
    if (/Linux/i.test(ua)) return 'Linux PC';
    return 'Unknown Device';
  };

  // Google OAuth Handler
  const handleGoogleLogin = () => {
    // Redirect to Emergent Auth with our app as redirect target
    const redirectUrl = window.location.origin + '/'; // Redirect to homepage after auth
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  // Check for Google OAuth callback (session_id in URL fragment)
  React.useEffect(() => {
    const processGoogleCallback = async () => {
      // Check if we have session_id in URL fragment
      const hash = window.location.hash;
      if (!hash || !hash.includes('session_id=')) {
        return;
      }

      setProcessingGoogle(true);

      try {
        // Extract session_id from URL fragment
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        
        if (!sessionId) {
          throw new Error('No session ID found');
        }

        console.log('Processing Google OAuth session...');

        // Exchange session_id for user data via our backend
        const response = await axios.post(
          `${API}/auth/google/session`,
          {},
          {
            headers: {
              'X-Session-ID': sessionId
            },
            withCredentials: true
          }
        );

        // Save session token
        if (response.data.token) {
          localStorage.setItem('session_token', response.data.token);
        }

        // Save user data
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        // Clean URL (remove fragment)
        window.history.replaceState(null, '', window.location.pathname);

        toast.success('Google login successful!');

        // Notify parent and navigate
        if (onLogin) {
          onLogin(response.data.user);
        }

        // Check if user came from shared recipe page
        const returnToShared = localStorage.getItem('return_to_shared');

        // Navigate to appropriate page
        if (returnToShared) {
          localStorage.removeItem('return_to_shared');
          navigate(`/shared/${returnToShared}`);
          toast.success('Du kan nu kopiere opskriften til din samling!');
        } else {
          navigate('/');
        }

      } catch (error) {
        console.error('Google auth error:', error);
        toast.error(getErrorMessage(error, 'Google login failed'));
        
        // Clean URL on error too
        window.history.replaceState(null, '', window.location.pathname);
      } finally {
        setProcessingGoogle(false);
      }
    };

    processGoogleCallback();
  }, [navigate, onLogin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const deviceId = getDeviceFingerprint();
      const deviceName = getDeviceName();
      
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
        device_id: deviceId,
        device_name: deviceName
      }, {
        withCredentials: true
      });

      // Show device limit warning if applicable
      if (response.data.device_limit) {
        const { current, max } = response.data.device_limit;
        if (current >= max) {
          toast.warning(t('auth.deviceLimitWarning', `Du er nu logget ind på maksimum ({{max}}) enheder. Ældre sessioner er blevet logget ud.`, { max }));
        }
      }
      
      toast.success(t('auth.loginSuccess', 'Login successful!'));
      
      // CRITICAL: Save session_token to localStorage (since httpOnly cookies don't work)
      if (response.data.session_token) {
        localStorage.setItem('session_token', response.data.session_token);
        console.log('[LoginPage] Saved session_token to localStorage');
      }
      
      // Save user's country and language preferences to localStorage
      // Use database values OR defaults if not set
      const userCountry = response.data?.user?.country || 'DK';
      const userLang = response.data?.user?.language || 'da';
      
      await updateUserPreferences(userCountry, userLang, false);
      
      // Update i18next immediately to ensure UI shows correct language
      if (window.i18n) {
        window.i18n.changeLanguage(userLang);
        console.log(`[LoginPage] Changed i18n language to: ${userLang}`);
      }
      
      console.log(`[LoginPage] Set user preferences - Country: ${userCountry}, Language: ${userLang}`);
      
      // Save user data - ensure we have valid user object
      if (response.data && response.data.user) {
        onLogin(response.data.user);
      } else {
        console.error('[LoginPage] No user data in response:', response.data);
        throw new Error('Login response missing user data');
      }
      
      // Check if user came from shared recipe page
      const returnToShared = localStorage.getItem('return_to_shared');
      // Or from a transactional email / deep link via ?next=
      const params = new URLSearchParams(window.location.search);
      const nextPath = params.get('next');
      const isSafeNext = nextPath && nextPath.startsWith('/') && !nextPath.startsWith('//');

      // Navigate to appropriate page (without reload to preserve localStorage)
      if (returnToShared) {
        localStorage.removeItem('return_to_shared');
        navigate(`/shared/${returnToShared}`);
        toast.success('Du kan nu kopiere opskriften til din samling!');
      } else if (isSafeNext) {
        navigate(nextPath);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(getErrorMessage(error, t('auth.loginFailed', 'Login failed')));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden py-8">
      {/* Video Background — bevaret fra tidligere design for at bevare
          SlushBooks visuelle identitet på landingpagen. */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="fixed top-0 left-0 w-screen h-screen object-cover"
        style={{zIndex: -1}}
      >
        <source src="/assets/slush-loop.mp4" type="video/mp4" />
      </video>
      {/* Subtil overlay så tekst-læsbarhed er bedre uden at slå videoen ud */}
      <div className="fixed top-0 left-0 w-screen h-screen bg-white/10 backdrop-blur-[1px]" style={{zIndex: -1}} />

      {processingGoogle ? (
        <div className="w-full max-w-md px-6 py-8">
          <div className="rounded-3xl shadow-xl p-8 text-center" style={{backgroundColor: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(10px)'}}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
            <p className="text-gray-700 font-medium">Processing Google login...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait...</p>
          </div>
        </div>
      ) : (
      <div className="w-full max-w-md px-6 py-4">
        {showLandingHero && (
          <>
            {/* Logo — matchet med SignupPage (w-72) så landing og signup/login
                opleves som samme "surface". */}
            <div className="text-center mb-4">
              <img
                src="/assets/slushbook.png"
                alt="SLUSHBOOK"
                className="w-72 mx-auto"
                style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))' }}
              />
            </div>

            {/* Hero-tekst — sælger produktet, ikke login-formularen */}
            <div className="text-center mb-6 px-2">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{textShadow: '2px 2px 8px rgba(0,0,0,0.4)'}}>
                Find din perfekte slushice-opskrift
              </h1>
              <p className="text-base md:text-lg text-white/95" style={{textShadow: '1px 1px 4px rgba(0,0,0,0.4)'}}>
                Opskrifter, ingrediens-match og AI-hjælp — alt til din slushmaskine.
              </p>
            </div>

            {/* Primær CTA — gæsteoplevelse som naturlig indgang */}
            <div className="mb-5">
              <button
                type="button"
                onClick={handleExploreAsGuest}
                data-testid="landing-explore-cta"
                className="group w-full py-5 rounded-2xl font-bold text-white text-xl shadow-2xl transition-all transform hover:scale-[1.02] active:scale-100 flex items-center justify-center gap-3"
                style={{
                  background: 'linear-gradient(90deg, #14b8a6 0%, #0891b2 50%, #2563eb 100%)',
                  boxShadow: '0 20px 40px -12px rgba(6, 182, 212, 0.55)',
                }}
              >
                <span>Se opskrifterne nu</span>
                <FaArrowRight className="transition-transform group-hover:translate-x-1" />
              </button>
              <p className="text-center text-xs text-white/90 mt-2" style={{textShadow: '1px 1px 3px rgba(0,0,0,0.4)'}}>
                Kom i gang uden konto · ingen betaling
              </p>
            </div>

            {/* 3 værdi-kort er fjernet per user 08-Jul-2026. Hero-tekst + CTA
                bevares midlertidigt indtil SlushBook-fortællingen er landet. */}
          </>
        )}

        {/* Logo for direkte-login-mode — matcher SignupPage nøjagtigt.
            Ingen ekstra "Log ind"-heading her; H2 inde i formularen viser
            konteksten, præcis som SignupPage viser "Opret din konto". */}
        {!showLandingHero && (
          <div className="text-center" style={{marginBottom: '20px'}}>
            <img
              src="/assets/slushbook.png"
              alt="SLUSHBOOK"
              className="w-72 mx-auto"
            />
          </div>
        )}

        {/* Login-panel — for direkte-mode matcher det SignupPage 1:1
            (padding p-6 sm:p-8, H2 inde i boxen). For landing-mode har det
            stadig collapsible-toggle øverst. */}
        <div className={`rounded-3xl shadow-xl overflow-hidden ${showLandingHero ? '' : ''}`} style={{backgroundColor: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(10px)'}}>
          {showLandingHero ? (
            <button
              type="button"
              onClick={() => setLoginExpanded(!loginExpanded)}
              data-testid="landing-toggle-login"
              className="w-full px-5 py-3 flex items-center justify-between hover:bg-white/40 transition-colors"
              aria-expanded={loginExpanded}
            >
              <span className="text-sm font-semibold text-gray-800">
                Har du allerede en konto? <span className="text-cyan-700 underline">Log ind</span>
              </span>
              {loginExpanded ? <FaChevronUp className="text-gray-500" /> : <FaChevronDown className="text-gray-500" />}
            </button>
          ) : null}

          {(loginExpanded || !showLandingHero) && (
            <div className={showLandingHero ? 'px-5 pb-5 pt-1 space-y-4' : 'p-6 sm:p-8'} data-testid="landing-login-panel">
              {!showLandingHero && (
                <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">{t('auth.signIn')}</h2>
              )}
              <form onSubmit={handleLogin} className={showLandingHero ? 'space-y-3' : 'space-y-4'}>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.email')}
                  required
                  data-testid="landing-email-input"
                  className="w-full px-4 py-3 border-2 rounded-xl"
                  style={{ backgroundColor: '#D4E157', borderColor: '#C0CA33' }}
                />
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={(e) => e.target.select()}
                    placeholder={t('auth.password')}
                    required
                    data-testid="landing-password-input"
                    className="w-full px-4 py-3 pr-12 border-2 rounded-xl"
                    style={{ backgroundColor: '#D4E157', borderColor: '#C0CA33' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800"
                  >
                    {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
                  </button>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="landing-login-submit"
                  className="w-full py-3 rounded-xl font-bold text-white text-lg"
                  style={{ backgroundColor: '#7CB342' }}
                >
                  {loading ? t('auth.loggingIn', 'Logging in...') : t('auth.signIn')}
                </Button>
              </form>

              <div className="flex justify-between text-sm">
                <Link to={`/signup${window.location.search || ''}`} className="text-cyan-700 hover:text-cyan-800 font-medium" data-testid="landing-signup-link">
                  {t('auth.createNewAccount', 'Opret ny konto')}
                </Link>
                <Link to="/forgot-password" className="text-cyan-700 hover:text-cyan-800 font-medium">
                  {t('auth.forgotPassword')}
                </Link>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white/80 text-gray-500">eller</span>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleGoogleLogin}
                data-testid="landing-google-btn"
                className="w-full py-3 rounded-xl font-medium border-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              >
                <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5 inline mr-2" />
                Fortsæt med Google
              </Button>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default LoginPage;
